import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../zodios/client";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { Button } from "../../components/core/basic/Button";
import { TextField } from "../../components/fields/base/TextField";
import { successToast } from "../../utils/toasts";
import { HoverInfo } from "../../components/core/basic/Tooltip";
import { AuthLayout } from "../../layouts/AuthLayout";
import { useTranslation } from "react-i18next";

interface UpdatePasswordFormData {
  token: string;
  newPassword: string;
}

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const { t } = useTranslation("auth");

  const navigate = useNavigate();

  const setNewPasswordMutation = useMutation({
    mutationFn: async (updatePasswordData: UpdatePasswordFormData) => {
      await apiClient.processPasswordReset(updatePasswordData);
    },
    onError: (error) => {
      if (typeof error.message === "string" && !error.message.includes("400")) {
        setError(error.message);
      } else {
        setError(
          t(
            ($) =>
              $.auth.resetPassword[
                "Something went wrong while setting your new password.\n\nPlease try again or contact customer success."
              ],
          ),
        );
      }
    },
    onSuccess: () => {
      successToast({ message: t(($) => $.auth.resetPassword["Password successfully updated."]) });
      void navigate({ to: "/" });
    },
  });

  return (
    <AuthLayout
      title={t(($) => $.auth.resetPassword["Reset your password"])}
      subtitle={t(($) => $.auth.resetPassword["Enter your password below."])}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (password !== confirmPassword) {
            setError(t(($) => $.auth.resetPassword["Password and password confirmation do not match."]));
            return;
          }

          const queryParams = new URLSearchParams(window.location.search);
          const token = queryParams.get("user_token");

          if (!token) {
            setError(t(($) => $.auth.resetPassword["A token is required"]));
            void navigate({ to: "/" });
            return;
          }

          setNewPasswordMutation.mutate({
            token,
            newPassword: password,
          });
        }}
      >
        <Flex gap={"medium"} direction={"column"}>
          <TextField
            label={
              <span className={css({ display: "flex", gap: "small" })}>
                {t(($) => $.auth.resetPassword.Password)}{" "}
                <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                  {t(
                    ($) =>
                      $.auth.resetPassword[
                        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
                      ],
                  )}
                </HoverInfo>
              </span>
            }
            type={"password"}
            onChange={(value) => {
              setPassword(value);
            }}
          />
          <TextField
            label={t(($) => $.auth.resetPassword["Confirm Password"])}
            type={"password"}
            onChange={(value) => {
              setConfirmPassword(value);
            }}
          />
        </Flex>
        <Button
          isPending={setNewPasswordMutation.isPending}
          isDisabled={!password.trim().length || !confirmPassword.trim().length}
          className={css({ width: "100%", marginTop: "large" })}
          type="submit"
        >
          {t(($) => $.auth.resetPassword["Reset Password"])}
        </Button>
      </form>
      <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
        <Link to="/">{t(($) => $.auth.resetPassword["Sign In Instead"])}</Link>
      </div>
      {error && <p className={css({ paddingY: "large", textAlign: "center" })}>{error}</p>}
    </AuthLayout>
  );
};

export default ResetPassword;
